import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '@/lib/utils';

const API = getApiUrl();

const STATUS_COLORS = {
  sent: 'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
  read: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-700',
  pending: 'bg-yellow-100 text-yellow-700',
};

export const WhatsAppPage = ({ user, t, buildingId = null }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('send');
  const [messages, setMessages] = useState([]);
  const [templateDefs, setTemplateDefs] = useState(null);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Send form state
  const [sendMode, setSendMode] = useState('template'); // 'text' | 'template'
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedLang, setSelectedLang] = useState('tr');
  const [phone, setPhone] = useState('');
  const [textBody, setTextBody] = useState('');
  const [templateVars, setTemplateVars] = useState({});

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
      navigate('/app');
      return;
    }
    fetchAll();
  }, [user, navigate, buildingId]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [msgRes, tplRes, usersRes] = await Promise.all([
        axios.get(`${API}/whatsapp/messages`, { withCredentials: true }),
        axios.get(`${API}/whatsapp/template-definitions`, { withCredentials: true }),
        axios.get(`${API}/apartments${buildingId ? `?building_id=${buildingId}` : ''}`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setMessages(msgRes.data);
      setTemplateDefs(tplRes.data);
      // Flatten apartment residents with phone numbers
      const apts = usersRes.data || [];
      const resList = [];
      for (const apt of apts) {
        if (apt.residents) {
          for (const r of apt.residents) {
            if (r.phone) {
              resList.push({
                name: r.name || r.username,
                phone: r.phone.replace(/\D/g, ''),
                unit: apt.unit_number,
                apartment_id: apt.id,
              });
            }
          }
        }
      }
      setResidents(resList);
      if (tplRes.data?.templates?.length && !selectedTemplate) {
        setSelectedTemplate(tplRes.data.templates[0].key);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [buildingId, selectedTemplate]);

  const currentTplDef = templateDefs?.details?.[selectedTemplate];
  const currentTplVars = currentTplDef?.variables || [];

  const handleSend = async () => {
    const cleanPhone = phone.replace(/\D/g, '');
    if (!cleanPhone) {
      toast.error(t('waPhoneRequired') || 'Phone number is required');
      return;
    }

    setSending(true);
    try {
      if (sendMode === 'text') {
        if (!textBody.trim()) {
          toast.error(t('waMessageRequired') || 'Message is required');
          setSending(false);
          return;
        }
        await axios.post(`${API}/whatsapp/send-text`, {
          recipient_phone: cleanPhone,
          message_body: textBody,
        }, { withCredentials: true });
        toast.success(t('waMessageSent') || 'Message sent');
        setTextBody('');
      } else {
        const vars = currentTplVars.map(v => templateVars[v] || '');
        if (vars.some(v => !v)) {
          toast.error(t('waFillAllVars') || 'Please fill all template variables');
          setSending(false);
          return;
        }
        await axios.post(`${API}/whatsapp/send-template`, {
          recipient_phone: cleanPhone,
          template_name: currentTplDef?.meta_template_name,
          language_code: selectedLang,
          template_variables: vars,
        }, { withCredentials: true });
        toast.success(t('waTemplateSent') || 'Template message sent');
        setTemplateVars({});
      }
      setPhone('');
      fetchAll();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`${t('waError') || 'Error'}: ${detail}`);
    } finally {
      setSending(false);
    }
  };

  const selectResident = (r) => {
    setPhone(r.phone);
    if (sendMode === 'template' && selectedTemplate === 'dues_reminder') {
      setTemplateVars(prev => ({
        ...prev,
        residentName: r.name,
        apartmentNumber: `D:${r.unit}`,
      }));
    }
  };

  const varLabel = (varName) => {
    const key = `waTemplateVar${varName.charAt(0).toUpperCase() + varName.slice(1)}`;
    return t(key) || varName;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-teal-50 to-emerald-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              data-testid="wa-back-btn"
              onClick={() => navigate('/app')}
              className="p-2 hover:bg-white/60 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t('waPageTitle') || 'WhatsApp'}
              </h1>
              <p className="text-sm text-gray-500">{t('waPageSubtitle') || 'Send notifications to residents'}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white/60 rounded-xl p-1 backdrop-blur-sm w-fit">
          {[
            { id: 'send', label: t('waSendTab') || 'Send Message' },
            { id: 'log', label: t('waLogTab') || 'Message Log' },
            { id: 'templates', label: t('waTemplatesTab') || 'Templates' },
          ].map(tab => (
            <button
              key={tab.id}
              data-testid={`wa-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-teal-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Send Tab */}
        {activeTab === 'send' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Send Form */}
            <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm">
              {/* Mode Toggle */}
              <div className="flex gap-2 mb-5">
                <button
                  data-testid="wa-mode-template"
                  onClick={() => setSendMode('template')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    sendMode === 'template'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t('waTemplateMode') || 'Template'}
                </button>
                <button
                  data-testid="wa-mode-text"
                  onClick={() => setSendMode('text')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    sendMode === 'text'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t('waTextMode') || 'Free Text'}
                </button>
              </div>

              {/* Phone Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('waRecipientPhone') || 'Recipient Phone'}
                </label>
                <input
                  data-testid="wa-phone-input"
                  type="text"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="905XXXXXXXXX"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-200 focus:border-green-400 outline-none transition-all"
                />
              </div>

              {sendMode === 'text' ? (
                /* Text Message */
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('waMessageBody') || 'Message'}
                  </label>
                  <textarea
                    data-testid="wa-text-body"
                    value={textBody}
                    onChange={e => setTextBody(e.target.value)}
                    rows={4}
                    maxLength={4096}
                    placeholder={t('waTextPlaceholder') || 'Type your message...'}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-200 focus:border-green-400 outline-none transition-all resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{textBody.length}/4096</p>
                </div>
              ) : (
                /* Template Message */
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('waSelectTemplate') || 'Template'}
                      </label>
                      <select
                        data-testid="wa-template-select"
                        value={selectedTemplate}
                        onChange={e => {
                          setSelectedTemplate(e.target.value);
                          setTemplateVars({});
                        }}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-200 focus:border-green-400 outline-none bg-white"
                      >
                        {templateDefs?.templates?.map(tpl => (
                          <option key={tpl.key} value={tpl.key}>{t(`waTemplate${tpl.key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')}`) || tpl.key}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('waLanguage') || 'Language'}
                      </label>
                      <select
                        data-testid="wa-lang-select"
                        value={selectedLang}
                        onChange={e => setSelectedLang(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-200 focus:border-green-400 outline-none bg-white"
                      >
                        {currentTplDef && Object.keys(currentTplDef.languages).map(lang => (
                          <option key={lang} value={lang}>{lang.toUpperCase()}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Template Variables */}
                  {currentTplVars.length > 0 && (
                    <div className="space-y-3 mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        {t('waVariables') || 'Variables'}
                      </label>
                      {currentTplVars.map(varName => (
                        <input
                          key={varName}
                          data-testid={`wa-var-${varName}`}
                          type="text"
                          value={templateVars[varName] || ''}
                          onChange={e => setTemplateVars(prev => ({ ...prev, [varName]: e.target.value }))}
                          placeholder={varLabel(varName)}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-200 focus:border-green-400 outline-none transition-all"
                        />
                      ))}
                    </div>
                  )}

                  {/* Template Preview */}
                  {currentTplDef && selectedLang && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                      <p className="text-xs font-medium text-green-700 mb-1">{t('waPreview') || 'Preview'}</p>
                      <p className="text-sm text-green-900">
                        {(() => {
                          let body = currentTplDef.languages[selectedLang]?.body || '';
                          currentTplVars.forEach((v, i) => {
                            body = body.replace(`{{${i + 1}}}`, templateVars[v] || `[${varLabel(v)}]`);
                          });
                          return body;
                        })()}
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Send Button */}
              <button
                data-testid="wa-send-btn"
                onClick={handleSend}
                disabled={sending}
                className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {sending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                )}
                {sending ? (t('waSending') || 'Sending...') : (t('waSendBtn') || 'Send WhatsApp Message')}
              </button>

              {sendMode === 'text' && (
                <p className="text-xs text-amber-600 mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                  {t('waTextWarning') || 'Free text messages only work if the recipient has messaged your business in the last 24 hours. Use templates for first-time messages.'}
                </p>
              )}
            </div>

            {/* Residents Sidebar */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                {t('waResidents') || 'Residents with Phone'}
              </h3>
              {residents.length === 0 ? (
                <p className="text-xs text-gray-400 py-4 text-center">
                  {t('waNoResidents') || 'No residents with phone numbers found'}
                </p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {residents.map((r, i) => (
                    <button
                      key={i}
                      data-testid={`wa-resident-${i}`}
                      onClick={() => selectResident(r)}
                      className={`w-full text-left p-3 rounded-xl text-sm transition-all hover:bg-green-50 border ${
                        phone === r.phone ? 'border-green-400 bg-green-50' : 'border-gray-100'
                      }`}
                    >
                      <div className="font-medium text-gray-800">{r.name}</div>
                      <div className="text-xs text-gray-500">D:{r.unit} &middot; {r.phone}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Message Log Tab */}
        {activeTab === 'log' && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {messages.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <p className="text-sm">{t('waNoMessages') || 'No messages sent yet'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{t('waDate') || 'Date'}</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{t('waRecipient') || 'Recipient'}</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{t('waType') || 'Type'}</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{t('waContent') || 'Content'}</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{t('waStatus') || 'Status'}</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{t('waSentBy') || 'Sent By'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {messages.map((msg, i) => (
                      <tr key={msg.id || i} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-5 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {msg.created_at ? new Date(msg.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-800 font-mono">{msg.recipient_phone}</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            msg.type === 'template' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {msg.type === 'template' ? (msg.template_name || 'template') : 'text'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-600 max-w-xs truncate">
                          {msg.content || (msg.template_variables ? msg.template_variables.join(', ') : '-')}
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[msg.status] || 'bg-gray-100 text-gray-600'}`}>
                            {msg.status}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-500">{msg.sent_by || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {templateDefs?.templates?.map(tpl => {
              const detail = templateDefs.details[tpl.key];
              return (
                <div key={tpl.key} className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-800">
                        {t(`waTemplate${tpl.key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')}`) || tpl.key}
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">{tpl.description}</p>
                    </div>
                    <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-medium uppercase">
                      {tpl.category}
                    </span>
                  </div>

                  <div className="mb-3">
                    <p className="text-xs font-medium text-gray-500 mb-1">{t('waVariables') || 'Variables'}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {tpl.variables.map(v => (
                        <span key={v} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-mono">
                          {`{{${v}}}`}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    {detail && Object.entries(detail.languages).map(([lang, info]) => (
                      <div key={lang} className="bg-gray-50 rounded-lg p-3">
                        <span className="inline-block px-1.5 py-0.5 bg-white border border-gray-200 rounded text-xs font-semibold text-gray-600 mb-1">
                          {lang.toUpperCase()}
                        </span>
                        <p className="text-xs text-gray-700 leading-relaxed">{info.body}</p>
                      </div>
                    ))}
                  </div>

                  <p className="text-xs text-gray-400 mt-3 font-mono">
                    Meta: <span className="text-gray-600">{tpl.meta_template_name}</span>
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
